<template>
  <div>
    <div :class="$style.container">
      <AppPopover v-show="isFocused" :class="$style.popover">
        <ul :class="$style['popover--invalid']" v-show="isInvalid">
          <li :class="$style.popover__message" v-show="noValue">
            <div>メールアドレスを入力してください</div>
          </li>
          <li
            :class="$style.popover__message"
            v-show="!noValue && invalidEmail"
          >
            <IconExclamationCircle
              :class="[$style.popover__icon, $style['popover__icon--error']]"
            />
            <div>メールアドレス形式が正しくありません</div>
          </li>
        </ul>
        <ul :class="$style['popover--valid']" v-show="!isInvalid">
          <li :class="$style.popover__message">
            <IconCheckCircle
              :class="[$style.popover__icon, $style['popover__icon--success']]"
            />
            <div>有効な形式のメールアドレスです</div>
          </li>
        </ul>
      </AppPopover>

      <div :class="$style.wrapper">
        <DolphinTextInput
          type="email"
          :class="$style.wrapper__input"
          v-model="value"
          @focus="isFocused = true"
          @blur="isFocused = true"
        />
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { ref, computed, watch } from 'vue'
import * as v from 'valibot'

import DolphinTextInput from '@/components/atoms/DolphinTextInput.vue'
import IconCheckCircle from '@/components/icons/IconCheckCircle.vue'
import IconExclamationCircle from '@/components/icons/IconExclamationCircle.vue'
import AppPopover from '@/components/atoms/AppPopover.vue'

export type ValidateChangeEventParams = {
  isValid: boolean
}

const props = defineProps<{ modelValue: string }>()

const emit = defineEmits<{
  (event: 'update:modelValue', value: string): void
  (event: 'validateChange', value: ValidateChangeEventParams): void
}>()

const isFocused = ref(false)

const EmailSchema = v.pipe(v.string(), v.email())

const invalidEmail = computed(() => {
  return !v.is(EmailSchema, props.modelValue)
})

const noValue = computed(() => props.modelValue.length === 0)
const isInvalid = computed(() => noValue.value || invalidEmail.value)

const onValueChange = (value: string): void => {
  emit('update:modelValue', value)
  emit('validateChange', {
    isValid: !isInvalid.value,
  } as ValidateChangeEventParams)
}

const value = computed({
  get: () => props.modelValue,
  set: (value: string): void => {
    emit('update:modelValue', value)
  },
})

watch(() => props.modelValue, onValueChange, {
  immediate: true,
})
</script>

<style lang="scss" module>
.wrapper {
  display: flex;
  box-sizing: border-box;
  align-items: center;
  padding: 10px 16px 11px;
  border: 1px solid $color-border-low-emphasis;
  background-color: $color-surface-primary;
  border-radius: $border-radius-v2-base;

  &__input {
    flex-grow: 1;
    padding: 0;
    border: 0;
  }
}

.popover {
  position: absolute;
  transform: translateY(-100%);

  &--invalid {
    color: $color-caution-accent;
  }

  &--valid {
    color: $color-success-object;
  }

  &__message {
    display: flex;
    align-items: center;
  }

  &__icon {
    margin-right: 0.5em;

    &--success {
      color: $color-success-accent;
    }

    &--error {
      color: $color-caution-accent;
    }
  }
}
</style>
